import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { DEMO_GUEST_PROGRESS, isScreenshotGuestMode } from '../../../lib/screenshotMode';

/**
 * Guest User Types
 */
export type UserAuthState = 'guest' | 'authenticated' | 'checking';

export interface GuestProgress {
  quizzesCompleted: number;
  totalScore: number;
  correctAnswers: number;
  totalQuestions: number;
  lastCategoryId: number | null;
  categoryProgress: Record<string, {
    quizzes: number;
    score: number;
    correctAnswers: number;
    totalQuestions: number;
  }>;
  lastPlayedAt: string;
  firstPlayedAt: string;
  dismissedPrompts: string[]; // Track which sign-up prompts user has dismissed
}

export interface GuestUserContextType {
  authState: UserAuthState;
  isGuest: boolean;
  guestProgress: GuestProgress | null;

  // Actions
  startGuestSession: () => Promise<void>;
  endGuestSession: () => void;
  updateGuestProgress: (update: Partial<GuestProgress>) => Promise<void>;
  recordQuizCompletion: (categoryId: number, score: number, correct: number, total: number) => Promise<void>;
  dismissPrompt: (promptId: string) => Promise<void>;
  hasSeenPrompt: (promptId: string) => boolean;
  clearGuestData: () => Promise<void>;

  // Migration
  getGuestDataForMigration: () => Promise<GuestProgress | null>;
}

const GuestUserContext = createContext<GuestUserContextType | undefined>(undefined);

const GUEST_STORAGE_KEY = '@guest_user_data';

const defaultGuestProgress: GuestProgress = {
  quizzesCompleted: 0,
  totalScore: 0,
  correctAnswers: 0,
  totalQuestions: 0,
  lastCategoryId: null,
  categoryProgress: {},
  lastPlayedAt: new Date().toISOString(),
  firstPlayedAt: new Date().toISOString(),
  dismissedPrompts: []
};

export const GuestUserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const screenshotGuestMode = isScreenshotGuestMode();
  const [authState, setAuthState] = useState<UserAuthState>(screenshotGuestMode ? 'guest' : 'checking');
  const [guestProgress, setGuestProgress] = useState<GuestProgress | null>(
    screenshotGuestMode ? DEMO_GUEST_PROGRESS : null
  );

  useEffect(() => {
    if (screenshotGuestMode) {
      return;
    }
    loadGuestData();
  }, [screenshotGuestMode]);

  const loadGuestData = async () => {
    try {
      const data = await AsyncStorage.getItem(GUEST_STORAGE_KEY);
      if (data) {
        const raw = JSON.parse(data);
        const parsed = {
          ...defaultGuestProgress,
          ...raw,
          dismissedPrompts: Array.isArray(raw.dismissedPrompts)
            ? raw.dismissedPrompts
            : [],
        };
        setGuestProgress(parsed);
        console.log('📊 Loaded guest progress:', parsed);
      }
    } catch (error) {
      console.error('Error loading guest data:', error);
    }
  };

  const saveGuestData = async (data: GuestProgress) => {
    try {
      await AsyncStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(data));
      setGuestProgress(data);
    } catch (error) {
      console.error('Error saving guest data:', error);
    }
  };

  const startGuestSession = async () => {
    console.log('👤 Starting guest session');
    setAuthState('guest');

    // Initialize guest data if not exists
    if (!guestProgress) {
      await saveGuestData(defaultGuestProgress);
    }
  };

  const endGuestSession = () => {
    console.log('👤 Ending guest session — returning to auth screen');
    setAuthState('checking');
  };

  const updateGuestProgress = async (update: Partial<GuestProgress>) => {
    const currentProgress = guestProgress || defaultGuestProgress;
    const updatedProgress = {
      ...currentProgress,
      ...update,
      lastPlayedAt: new Date().toISOString()
    };
    await saveGuestData(updatedProgress);
  };

  const recordQuizCompletion = async (
    categoryId: number,
    score: number,
    correct: number,
    total: number
  ) => {
    const currentProgress = guestProgress || defaultGuestProgress;

    // Update overall stats
    const updatedProgress: GuestProgress = {
      ...currentProgress,
      quizzesCompleted: currentProgress.quizzesCompleted + 1,
      totalScore: currentProgress.totalScore + score,
      correctAnswers: currentProgress.correctAnswers + correct,
      totalQuestions: currentProgress.totalQuestions + total,
      lastCategoryId: categoryId,
      lastPlayedAt: new Date().toISOString()
    };

    // Update category-specific progress
    const categoryKey = categoryId.toString();
    const categoryData = currentProgress.categoryProgress[categoryKey] || {
      quizzes: 0,
      score: 0,
      correctAnswers: 0,
      totalQuestions: 0
    };

    updatedProgress.categoryProgress[categoryKey] = {
      quizzes: categoryData.quizzes + 1,
      score: categoryData.score + score,
      correctAnswers: categoryData.correctAnswers + correct,
      totalQuestions: categoryData.totalQuestions + total
    };

    await saveGuestData(updatedProgress);
    console.log('📊 Guest quiz completed. Total quizzes:', updatedProgress.quizzesCompleted);
  };

  const dismissPrompt = async (promptId: string) => {
    const currentProgress = guestProgress || defaultGuestProgress;
    if (!currentProgress.dismissedPrompts.includes(promptId)) {
      const updatedProgress = {
        ...currentProgress,
        dismissedPrompts: [...currentProgress.dismissedPrompts, promptId]
      };
      await saveGuestData(updatedProgress);
      console.log('🚫 Dismissed prompt:', promptId);
    }
  };

  const hasSeenPrompt = (promptId: string): boolean => {
    return guestProgress?.dismissedPrompts.includes(promptId) || false;
  };

  const clearGuestData = async () => {
    try {
      await AsyncStorage.removeItem(GUEST_STORAGE_KEY);
      setGuestProgress(null);
      console.log('🗑️ Cleared guest data');
    } catch (error) {
      console.error('Error clearing guest data:', error);
    }
  };

  const getGuestDataForMigration = async (): Promise<GuestProgress | null> => {
    try {
      const data = await AsyncStorage.getItem(GUEST_STORAGE_KEY);
      if (data) {
        return JSON.parse(data);
      }
      return null;
    } catch (error) {
      console.error('Error getting guest data for migration:', error);
      return null;
    }
  };

  const value: GuestUserContextType = {
    authState,
    isGuest: authState === 'guest',
    guestProgress,
    startGuestSession,
    endGuestSession,
    updateGuestProgress,
    recordQuizCompletion,
    dismissPrompt,
    hasSeenPrompt,
    clearGuestData,
    getGuestDataForMigration
  };

  return (
    <GuestUserContext.Provider value={value}>
      {children}
    </GuestUserContext.Provider>
  );
};

export const useGuestUser = (): GuestUserContextType => {
  const context = useContext(GuestUserContext);
  if (!context) {
    throw new Error('useGuestUser must be used within GuestUserProvider');
  }
  return context;
};
